import { useEffect, useRef, type ReactNode } from "react";

/**
 * A pop-up over the page, so editing one row of a long list doesn't mean
 * scrolling away from it. The browser's own <dialog>: it traps focus, dims
 * the page behind, and closes on Escape. Clicking outside does not close
 * it -- a stray click should not throw away half-typed edits.
 */
export function Modal({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => { if (d?.open) d.close(); };
  }, []);

  return (
    <dialog
      ref={ref}
      className="modal"
      aria-label={label}
      // Escape is the only way the browser closes it; treat it as Cancel and
      // let the parent unmount us. (The dialog's own "close" event is not
      // used: it also fires when we close it ourselves on unmount.)
      onCancel={(e) => { e.preventDefault(); onClose(); }}
    >
      {children}
    </dialog>
  );
}
